<template>
  <Main pageClass="narrow-ui">
    <h1>Quote</h1>
    <!-- Step 1 -->
    <b-card>
      <h2 class="step-title">Destination</h2>
      <div class="search-wrapper">
        <b-form-input id="addressInput" v-model="searchInput" size="lg" autocomplete="false" placeholder="Start typing the destinations address..."/>
        <button v-if="searchInput" class="clear-btn" @click="reset()">Clear</button>
      </div>
    </b-card>
    <!-- Step 2 -->
    <b-card v-if="step >= 2" class="mt-3">
      <h2 class="step-title">Product and Delivery</h2>
      <div class="form-inline">
        <div>
          <b-form-select v-model="productType" :options="productTypeOptions" />
        </div>
        <div class="ml-2">
          <b-form-select v-model="transportMethod" :options="transportMethodOptions" :disabled="!productType" />
        </div>
        <div class="ml-2">
          <b-form-select v-model="deliveryMethod" :options="deliveryMethodOptions" :disabled="!transportMethod" />
        </div>
        <div class="ml-2">
          <b-input-group :class="{'disabled': !deliveryMethod}" append="Tons">
            <b-form-input v-model="quantity" :readonly="!productType && !transportMethod && !deliveryMethod" type="number" placeholder="0" />
          </b-input-group>
        </div>
        <div class="ml-2">
          <button :disabled="!canFetchQuote" class="btn btn-primary btn-block" @click="fetchQuote()">Get Rates</button>
        </div>
      </div>
    </b-card>
    <!-- Step 3 -->
    <b-overlay :show="loading" rounded="sm" spinner-variant="primary">
      <b-card v-if="step >= 3" class="mt-3">
        <h2 class="step-title">Rates Available</h2>
        <b-row v-if="!loading">
          <b-col>
            <div v-if="!rates.length">
              <p>No transport quotes available for the selected address or product.</p>
            </div>
            <ul v-if="rates" class="transport-rates">
              <li v-for="(rate,i) in rates" :key="`rate_${i}`" :value="i" name="rates-radios">
                <header class="rate-header d-flex justify-content-between align-items-center mb-3">
                  <h3 class="rate-name">{{ rate.transporter }}</h3>
                  <span v-if="rate.fulfillable" class="badge badge-success badge-sm" variant="success">In Stock</span>
                  <span v-if="!rate.fulfillable" class="badge badge-danger badge-sm">No Stock</span>
                </header>
                <p class="quote-info">
                  Rate Type <span>{{ rate.type }}</span>
                </p>
                <p class="quote-info">
                  Origin <span>{{ rate.origin }}</span>
                </p>
                <p class="quote-info">
                  Distance to Destination <span>{{ warehouseDistances[0].distanceToDestination.text }}</span>
                </p>
                <p class="quote-info">
                  Distance to Nearest Town ({{ nearestTown.name }}) <span>{{ warehouseDistances[0].distanceToNearestTown.text }}</span>
                </p>
                
                <h5>Freight Cost</h5>
                <div class="price-block mb-3">
                  <div class="price-name">{{ rate.freightFormula }}</div>
                  <div class="cost">
                    <span class="unit-cost">{{ (rate.freight).toLocaleString('en-US', {style: 'currency',currency: 'USD',}) }}</span>
                  </div>
                </div>
                <h5 class="d-flex align-content-center justify-content-between">Product Cost <span class="helper-text">Total includes freight</span></h5>
                <div v-for="(price,index) in rate.pricing" :key="index" class="price-block">
                  <div class="price-name">
                    {{ price.description }}
                    <span>{{ price.type === 'spot' ? 'Today\'s Price' : 'Month Spread Price' }}</span>
                  </div>
                  <div class="cost">
                    <span class="unit-cost">{{ (price.price).toLocaleString('en-US', {style: 'currency',currency: 'USD',}) }} T</span>
                    <span class="total-cost"><span>Total </span>{{ (price.totalCost).toLocaleString('en-US', {style: 'currency',currency: 'USD',}) }}</span>
                  </div>
                </div>
              </li>
            </ul>
          </b-col>
          <b-col v-if="warehouseDistances">
            <div id="map" ref="map"/>
          </b-col>
        </b-row>
      </b-card>
    </b-overlay>
  </Main>
</template>

<script>
import Main from "@/views/Main.vue";
import { newQuote, quoteForCoordinates } from '@/services/api';

export default {
  name: "Dashboard",
  components: {
    Main
  },
  extends: Main,
  data() {
    return {
      loading: false,
      step: 1,
      productTypeOptions: [
        { value: null, text: 'Select a product' }
      ],
      transportMethodOptions: [
        { value: null, text: 'Select a transport method' }
      ],
      deliveryMethodOptions: [
        { value: null, text: 'Select a delivery method' }
      ],
      searchInput: '',
      latitude: null,
      longitude: null,
      productType: null,
      transportMethod: null,
      deliveryMethod: null,
      quantity: null,
      nearestTown: null,
      warehouseDistances: [],
      rates: []
    };
  },
  computed: {
    canFetchQuote() {
      if(this.productType && this.transportMethod && this.quantity > 0){
        return true;
      }
      return false;
    }
  },
  mounted() {
    this.initAutocomplete();
    this.initOptions();
  },
  methods: {
    async initOptions() {
      try {
        const result = await newQuote();
        if(result){
          result.products.forEach((opt) => this.productTypeOptions.push({value: opt.id, text: opt.code.toLowerCase()}));
          result.transportMethods.forEach((opt) => this.transportMethodOptions.push({value: opt.id, text: opt.type.toLowerCase()}));
          result.deliveryMethods.forEach((opt) => this.deliveryMethodOptions.push({value: opt.id, text: opt.method.toLowerCase()}));
        }
      }catch(e){
        console.log(e);
      }
    },
    initAutocomplete() {
      const element = document.getElementById('addressInput');
      const options = {
        componentRestrictions: {country: "nz"}
      };
      const autocomplete = new google.maps.places.Autocomplete(element,options);
      autocomplete.addListener('place_changed', () => {
        const place = autocomplete.getPlace();
        if (!place.geometry) {
          this.address = ''
          this.latitude = ''
          this.longitude = ''
          return
        }
        this.searchInput = place.formatted_address;
        this.latitude = place.geometry.location.lat();
        this.longitude = place.geometry.location.lng();
        this.step = 2;
      })
    },
    async fetchQuote() {
      this.step = 3;
      const params = {};
      params.latitude = this.latitude;
      params.longitude = this.longitude;
      params.type = this.transportMethod;
      params.method = this.deliveryMethod;
      params.product = this.productType;
      params.qty = this.quantity;
      try {
        this.loading = true;
        const result = await quoteForCoordinates(params);
        console.log(result);
        if(result){
          this.nearestTown = result.nearestTown;
          this.warehouseDistances = result.warehouseDistances;
          this.rates = result.rates;
        }
        this.displayMap();
      }catch(e){
        console.log(e);
      }
      this.loading = false;
    },
    async displayMap() {
      await new Promise(resolve => setTimeout(resolve, 500));
      const element = document.getElementById('map');
      const destination = new google.maps.LatLng(this.latitude, this.longitude);
      const nearestTown = new google.maps.LatLng(this.nearestTown.latitude, this.nearestTown.longitude);
      const map = new google.maps.Map(element, {zoom: 5, center: destination});
      const destinationMarker = new google.maps.Marker({position: destination, map: map, title: 'Destination', icon: '/static/icons/destination-marker.png'});
      const nearestTownMarker = new google.maps.Marker({position: nearestTown, map: map, title: `Nearest Town: ${this.nearestTown.name}`, icon: '/static/icons/town-marker.png'});
      const infoWindow = new google.maps.InfoWindow();

      google.maps.event.addListener(destinationMarker, 'click', function() {
        infoWindow.setContent(this.get('title'));
        infoWindow.open(map,this);
      });

      google.maps.event.addListener(nearestTownMarker, 'click', function() {
        infoWindow.setContent(this.get('title'));
        infoWindow.open(map,this);
      });
        
      this.warehouseDistances.forEach((warehouse)=>{
        const warehouseMarker = new google.maps.Marker({position: new google.maps.LatLng(warehouse.latitude, warehouse.longitude), map: map, title: `Warehouse: ${warehouse.name}`, icon: '/static/icons/warehouse-marker.png'});
        google.maps.event.addListener(warehouseMarker, 'click', function() {
          infoWindow.setContent(this.get('title'));
          infoWindow.open(map,this);
        });
      });
    },
    reset() {
      this.searchInput = '';
      this.latitude = null;
      this.longitude = null;
      this.step = 1;
      this.productType = null;
      this.transportMethod = null;
      this.quantity = null;
    }
  }
};
</script>
<style lang="scss">
@import "@/assets/styles/common/_variables.scss";
.loader {
  display: block;
  width: 3rem;
  height: 3rem;
  margin: rem-calc(30) auto;
}
.step-title {
  display: flex;
  align-items: center;
  margin-bottom: rem-calc(20);
  color: $text-color-normal;
  font-size: 1.4rem;
  .step-number {
    display: inline-block;
    background: $text-color-normal;
    color: #fff;
    width: rem-calc(32);
    height: rem-calc(32);
    border-radius: 20px;
    align-content: center;
    margin-right: rem-calc(10);
    text-align: center;
    line-height: rem-calc(32);
    font-size: 1.2rem;
  }
}
.search-wrapper {
  position: relative;
  .clear-btn {
    position: absolute;
    right: rem-calc(15);
    top: 50%;
    transform: translateY(-50%);
    background: none;
    border: none;
    color: $brand-primary;
  }
}
.transport-rates {
  list-style: none;
  margin: 0;
  padding: 0;
  li {
    font-size: 1rem;
    padding: rem-calc(16);
    border-radius: rem-calc(4);
    border: 1px solid $ui-gray-lighter;
    margin-bottom: rem-calc(8);
    color: $text-color-normal;
    &.selected {
      border-color: $brand-primary;
    }
    &:last-child {
      margin-bottom: 0;
    }
    .rate-name {
      font-weight: $font-weight-bold;
      font-size: 1.5rem;
      color: $text-color-normal;
      margin: 0;
    }
    .quote-info {
      display: flex;
      justify-content: space-between;
      align-items: center;
      color: $ui-gray-mid;
      margin: 0;
      padding: 0;
      font-size: .8rem;
      padding-bottom: rem-calc(8);
      margin-bottom: rem-calc(8);
      border-bottom: 1px solid $ui-gray-lighter;
      span {
        display: block;
        color: $ui-gray;
        font-size: .8rem;
        font-weight: $font-weight-semibold;
      }
      &:last-of-type {
        margin-bottom: rem-calc(16);
      }
    }
    .price-block {
      display: flex;
      align-items: center;
      justify-content: space-between;
      padding: rem-calc(8) rem-calc(12);
      border-radius: rem-calc(4);
      background: $ui-gray-lightest;
      font-size: 1rem;
      margin-bottom: rem-calc(16);
      &:last-child {
        margin-bottom: 0;
      }
      .price-name {
        font-size: 1rem;
        font-weight: $font-weight-semibold;
        span {
          display: block;
          font-size: .7rem;
          font-weight: $font-weight-regular;
          color: $ui-gray-mid;
        }
      }
      .cost {
        text-align: right;
        .cost-label {
          display: block;
          font-size: .75rem;
          color: $ui-gray-mid;
        }
        .unit-cost {
          display: block;
          font-size: 1rem;
          font-weight: $font-weight-bold;
          color: $primary;
        }
        .total-cost {
          display: block;
          font-size: .75rem;
          font-weight: $font-weight-bold;
          color: $ui-gray-mid;
          span {
            font-weight: $font-weight-regular;
          }
        }
      }
    }
  }
  .helper-text {
    font-size: .8rem;
    font-weight: $font-weight-regular;
    float: right;
  }
}
#map {
  height: 300px;
}
</style>



// WEBPACK FOOTER //
// src/views/Quote.vue